import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Modem from '@assets/icons/modem.webp';
import {
  Grid,
  List,
  PowerIcon,
  ArrowDownUp,
  TriangleAlert,
  CircleCheck,
  LucideCirclePlus,
  LucideMinusCircle,
  TriangleAlertIcon,
  Map,
  RotateCw,
  Database,
} from 'lucide-react';
import Tooltip from '@mui/material/Tooltip';
import { ResumeGrid, Info, LeftContent } from '../styles';
import Typography from '@mui/material/Typography';
import { Button } from '@/components/Button';
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import api from '@/services/api';
import { toProxyImageUrl } from '@/utils/formatters';
import { useTheme } from '@mui/material/styles';
import { StatusBall } from '@/pages/ListEquipments/styles';
import Skeleton from '@mui/material/Skeleton';
import AlertDialog from '@/components/AlertDialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { DynamicIcon, type IconName } from 'lucide-react/dynamic';
import {
  GeneralViewBox,
  HeaderBox,
  MapButton,
  MobileExpandedButton,
  ModemContainer,
  ModemImage,
  ModemImageWrapper,
  ModelImageHover,
  RebootButton,
  ResetButton,
  RouterBox,
  StatusBox,
  TitleField,
  UpdateButton,
  ValueField,
  ModelInfoSection,
  ToggleButtonGroupContainer,
  ControlsStack,
  ButtonsRow,
  MobileActionsBox,
  QuickActionsRow,
} from './styles';
import Card from '@/components/Card';
import DescriptionPopper from '@/components/DescriptionPopper';
import { DescriptionComponent } from '../Tree/styles';
import { useEquipmentContext } from '@/context/EquipmentContext';
import { formatIsoDate } from '@/utils/timeFormatter';

/** Equipment resume info */
function Resume({
  modeView,
  setModeView,
}: {
  modeView: string;
  setModeView: Dispatch<SetStateAction<string>>;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentPing, setCurrentPing] = useState(-999);
  const [status, setStatus] = useState(false);
  //const [loading, setLoading] = useState(true);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [value, setValue] = useState(false);
  const [openNotificationDialog, setOpenNotificationDialog] = useState(false);
  const [valueNotification, setValueNotification] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [dialogType, setDialogType] = useState<'reboot' | 'reset' | null>(null);
  const [syncingErp, setSyncingErp] = useState(false);
  const [syncingEquipment, setSyncingEquipment] = useState(false);

  const { equipment, refetchEquipment, syncErpEquipment, loading, setLoading } =
    useEquipmentContext();

  const isLg = useMediaQuery(theme.breakpoints.down(1400));
  const isSm = useMediaQuery(theme.breakpoints.down(700));

  // model image from backend, falling back to the default modem picture
  const [modelImageError, setModelImageError] = useState(false);
  useEffect(() => {
    setModelImageError(false);
  }, [equipment?.model?.urlImage]);
  const modelImage = toProxyImageUrl(equipment?.model?.urlImage);
  const showModelImage = Boolean(modelImage) && !modelImageError;

  useEffect(() => {
    setStatus(equipment?.online ?? true);
    console.log('equipment changed: ', equipment);
  }, [equipment]);

  useEffect(() => {
    if (!equipment || equipment?.ipAddress === 'string') return;
    const eventSource = new EventSource(
      `/api/notify/ping/${equipment.ipAddress}`
    );

    setLoading(false);

    eventSource.addEventListener('Ping', (event) => {
      const data = JSON.parse(event.data);

      setCurrentPing(data?.latency);
    });

    eventSource.onerror = (err) => {
      console.error('Erro SSE:', err);
      eventSource.close();
    };

    return () => {
      eventSource.close();
    };
  }, [equipment, setLoading]);

  const fields: (keyof NetworkInterfaceType)[] = [
    'id',
    'manufacturer',
    'productClass',
    'oui',
    'serialNumber',
    'hardwareVersion',
    'softwareVersion',
    'ipAddress',
    'rootParameters',
    'createdAt',
    'lastInform',
  ];

  const iconsList: Partial<Record<keyof NetworkInterfaceType, IconName>> = {
    id: 'id-card',
    manufacturer: 'factory',
    productClass: 'microchip',
    oui: 'globe',
    hardwareVersion: 'microchip',
    softwareVersion: 'microchip',
    ipAddress: 'globe',
    rootParameters: 'grid',
    createdAt: 'calendar',
    lastInform: 'clock',
    serialNumber: 'square-asterisk',
  };

  /** Makes a POST request to reboot device and controls send, loading and receiving*/
  useEffect(() => {
    if (value && equipment) {
      api.post(`/devices/${equipment.id}/reboot`).then((response) => {
        if (response.status === 200) {
          enqueueSnackbar(t('equipment.resume.deviceRebooting'), {
            variant: 'info',
          });
          // device goes offline while rebooting, until the SSE reports back
          setStatus(false);
          const eventSource = new EventSource(
            `/api/notify/reboot/${equipment.id}`
          );

          eventSource.addEventListener('Reboot', (event) => {
            const data = JSON.parse(event.data);

            setStatus(data?.online);
            if (data?.online) {
              setOpenNotificationDialog(true);
            }
          });

          eventSource.onerror = (err) => {
            console.error('Erro SSE:', err);
            eventSource.close();
          };

          return () => {
            eventSource.close();
          };
        }
      });
      if (!valueNotification) setValue(false);
    }
  }, [value, equipment, valueNotification]);

  const handleSyncErp = async () => {
    if (!equipment) return;
    setSyncingErp(true);
    try {
      await syncErpEquipment();
      enqueueSnackbar(
        t(
          'equipment.resume.syncErpSuccess',
          'Dados do ERP sincronizados com sucesso'
        ),
        { variant: 'success' }
      );
    } catch (error: any) {
      console.error(error);
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        t('equipment.resume.syncErpError', 'Erro ao sincronizar dados do ERP');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSyncingErp(false);
    }
  };

  const handleSyncEquipment = async () => {
    if (!equipment) return;
    setSyncingEquipment(true);
    try {
      await refetchEquipment();
      enqueueSnackbar(
        t(
          'equipment.resume.syncEquipmentSuccess',
          'Dados do equipamento sincronizados com sucesso'
        ),
        { variant: 'success' }
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        t(
          'equipment.resume.syncEquipmentError',
          'Erro ao sincronizar dados do equipamento'
        ),
        { variant: 'error' }
      );
    } finally {
      setSyncingEquipment(false);
    }
  };

  const handleModeView = (
    _: React.MouseEvent<HTMLElement>,
    newModeView: string
  ) => {
    setModeView(newModeView);
  };

  if (!equipment) {
    return <Card>{t('equipment.resume.loadingEquipment')}</Card>;
  }

  return (
    <LeftContent>
      <HeaderBox>
        {!isSm && (
          <ModemContainer>
            {/** Router Status */}
            <ModemImageWrapper>
              <ModemImage src={Modem} />
              {showModelImage && modelImage && (
                <ModelImageHover
                  src={modelImage}
                  alt={equipment.model?.alias || equipment.model?.key}
                  onError={() => setModelImageError(true)}
                />
              )}
            </ModemImageWrapper>
            <RouterBox>
              <StatusBall online={status}>
                <div className="blink" />
              </StatusBall>
              <StatusBox
                variant="caption"
                color={
                  status
                    ? theme.palette.status.online
                    : theme.palette.status.offline
                }
              >
                {status
                  ? t('equipment.resume.online')
                  : t('equipment.resume.offline')}
              </StatusBox>
            </RouterBox>
          </ModemContainer>
        )}

        {/** General View */}
        <ModelInfoSection>
          <GeneralViewBox>
            <Typography
              variant="h5"
              fontWeight={600}
              color={theme.palette.text.primary}
              lineHeight={1.1}
              mb="4px"
            >
              {equipment && equipment?.model?.alias
                ? equipment.model?.alias
                : equipment.manufacturer + ' ' + equipment.productClass}
            </Typography>
            <Tooltip
              title={t(
                'equipment.resume.syncEquipment',
                'Sincronizar Dados do Equipamento'
              )}
              arrow
              placement="top"
            >
              <UpdateButton
                onClick={handleSyncEquipment}
                style={{
                  backgroundColor: syncingEquipment
                    ? theme.palette.grey['03']
                    : undefined,
                  cursor: syncingEquipment ? 'wait' : 'pointer',
                }}
              >
                <RotateCw size={14} />
              </UpdateButton>
            </Tooltip>
            <Tooltip
              title={t('equipment.resume.syncErp', 'Sincronizar Dados do ERP')}
              arrow
              placement="top"
            >
              <UpdateButton
                onClick={handleSyncErp}
                style={{
                  backgroundColor: syncingErp
                    ? theme.palette.grey['03']
                    : undefined,
                  cursor: syncingErp ? 'wait' : 'pointer',
                }}
              >
                <Database size={14} />
              </UpdateButton>
            </Tooltip>
          </GeneralViewBox>

          <Stack>
            <ResumeGrid isExpanded={isExpanded}>
              {/** Resume - Ping */}
              <Info>
                <ArrowDownUp size={18} />
                <TitleField>{t('equipment.resume.ping')}:</TitleField>
                {loading ? (
                  <Skeleton width="100%" height={18} variant="text" />
                ) : (
                  <ValueField>{currentPing} </ValueField>
                )}
              </Info>

              {/** General resume infos fields*/}
              {fields.map((field) => (
                <Info key={String(field)}>
                  <DynamicIcon
                    name={
                      (iconsList[field as keyof NetworkInterfaceType] ??
                        'grid') as IconName
                    }
                    size={18}
                  />
                  <TitleField>
                    {t(`equipment.resume.${field as string}`) + `: `}
                  </TitleField>
                  {loading ? (
                    <Skeleton width="100%" height={18} variant="text" />
                  ) : (
                    <ValueField>
                      {field === 'createdAt' || field === 'lastInform'
                        ? formatIsoDate(
                            String(equipment[field as keyof EquipmentType])
                          )
                        : String(equipment[field as keyof EquipmentType] ?? '')}
                    </ValueField>
                  )}
                </Info>
              ))}
            </ResumeGrid>

            {/** Displays when it's mobile */}
            {isLg && (
              <Stack direction="column" gap="8px" mt="10px">
                <MobileActionsBox>
                  <MobileExpandedButton
                    variant={'secondary'}
                    onClick={() => setIsExpanded(!isExpanded)}
                    startIcon={
                      isExpanded ? (
                        <LucideMinusCircle
                          size="18px"
                          color={theme.palette.white['06']}
                        />
                      ) : (
                        <LucideCirclePlus
                          size="18px"
                          color={theme.palette.white['06']}
                        />
                      )
                    }
                  >
                    <Stack alignItems="center" gap="4px">
                      <Typography
                        fontWeight={600}
                        color={theme.palette.white['06']}
                      >
                        {isExpanded
                          ? t('equipment.resume.seeLess')
                          : t('equipment.resume.seeMore')}
                      </Typography>
                    </Stack>
                  </MobileExpandedButton>

                  <QuickActionsRow>
                    <DescriptionPopper
                      description={
                        <DescriptionComponent>
                          {t('equipment.resume.reboot')}
                        </DescriptionComponent>
                      }
                      placement="top"
                    >
                      <RebootButton
                        label={<PowerIcon size="18px" />}
                        aria-label={t('equipment.resume.reboot')}
                        onClick={() => setOpenResetDialog(true)}
                      />
                    </DescriptionPopper>
                    <DescriptionPopper
                      description={
                        <DescriptionComponent>
                          {t('equipment.resume.reset')}
                        </DescriptionComponent>
                      }
                      placement="top"
                    >
                      <ResetButton
                        label={<TriangleAlertIcon size="18px" />}
                        aria-label={t('equipment.resume.reset')}
                        disabled
                      />
                    </DescriptionPopper>

                    <DescriptionPopper
                      description={
                        <DescriptionComponent>
                          {t('equipment.resume.seeOnMap')}
                        </DescriptionComponent>
                      }
                      placement="top"
                    >
                      <MapButton
                        label={<Map size="18px" />}
                        aria-label={t('equipment.resume.seeOnMap')}
                        disabled={!equipment?.customerData}
                        onClick={() => {
                          if (
                            equipment?.customerData?.lat &&
                            equipment?.customerData?.lon
                          ) {
                            navigate('/map', {
                              state: {
                                data: equipment,
                              },
                            });
                          } else {
                            enqueueSnackbar(
                              t('equipment.resume.mapDataNotAvailable'),
                              {
                                variant: 'warning',
                              }
                            );
                          }
                        }}
                      />
                    </DescriptionPopper>
                  </QuickActionsRow>
                </MobileActionsBox>

                <ToggleButtonGroupContainer>
                  <ToggleButtonGroup
                    color="primary"
                    value={modeView}
                    exclusive
                    onChange={handleModeView}
                    aria-label="Platform"
                    style={{ height: '36px' }}
                  >
                    <ToggleButton value="grid">
                      <Grid size="16px" />
                      {!isSm && t('equipment.resume.grade')}
                    </ToggleButton>
                    <ToggleButton value="list">
                      <List size="16px" />
                      {!isSm && t('equipment.resume.list')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </ToggleButtonGroupContainer>
              </Stack>
            )}
          </Stack>
        </ModelInfoSection>
      </HeaderBox>

      {/** Resume controls */}
      {!isLg && (
        <ControlsStack>
          <ButtonsRow>
            <Button
              startIcon={<PowerIcon size="18px" />}
              variant="secondary"
              onClick={() => {
                setDialogType('reboot');
                setOpenResetDialog(true);
              }}
            >
              {t('equipment.resume.reboot')}
            </Button>
            <Button
              startIcon={<TriangleAlert size="18px" />}
              variant="red"
              disabled
            >
              {t('equipment.resume.reset')}
            </Button>
          </ButtonsRow>
          <ToggleButtonGroupContainer fullWidth>
            <ToggleButtonGroup
              color="primary"
              value={modeView}
              exclusive
              onChange={handleModeView}
              aria-label="Platform"
              style={{ height: '36px' }}
            >
              <ToggleButton value="grid">
                <Grid size="16px" />
                {t('equipment.resume.grade')}
              </ToggleButton>
              <ToggleButton value="list">
                <List size="16px" />
                {t('equipment.resume.list')}
              </ToggleButton>
            </ToggleButtonGroup>
          </ToggleButtonGroupContainer>
          <Button
            disabled={!equipment?.customerData}
            onClick={() => {
              if (
                equipment?.customerData?.lat &&
                equipment?.customerData?.lon
              ) {
                navigate('/map', {
                  state: {
                    data: equipment,
                  },
                });
              } else {
                enqueueSnackbar(t('equipment.resume.mapDataNotAvailable'), {
                  variant: 'warning',
                });
              }
            }}
          >
            <Stack gap="8px" direction="row" alignItems="center">
              <Map size={18} />
              {t('equipment.resume.seeOnMap')}
            </Stack>
          </Button>
        </ControlsStack>
      )}

      {/** Reboot confirm dialog alert */}
      <AlertDialog
        title={dialogType ?? t('equipment.resume.confirmRebootTitle')}
        open={openResetDialog}
        setOpen={setOpenResetDialog}
        setValue={setValue}
        content={
          <Typography>
            {t('equipment.resume.confirmReboot')}
            {equipment?.model?.alias
              ? ` ` + equipment?.model?.alias
              : ` ` + equipment?.manufacturer + equipment?.oui}
          </Typography>
        }
        confirmText={dialogType == 'reboot' ? t('common.confirm') : 'Confirmar'}
        cancelText={dialogType == 'reboot' ? t('common.cancel') : 'Cancelar'}
      />

      {/** Reboot success confirmation dialog alert */}
      <AlertDialog
        icon={<CircleCheck color={theme.palette.status.online} />}
        title={t('equipment.resume.alertSuccessReboot')}
        content={
          <Typography>
            {t('equipment.resume.alertSuccessMessageRebootP1')}
            {equipment?.model?.alias
              ? equipment?.model?.alias
              : equipment?.manufacturer + equipment?.oui}{' '}
            {t('equipment.resume.alertSuccessMessageRebootP2')}
          </Typography>
        }
        open={openNotificationDialog}
        setOpen={setOpenNotificationDialog}
        setValue={setValueNotification}
        cancelText={t('equipment.resume.closeRebootSuccessMessage')}
        showConfirmButton={false}
        titleColor={theme.palette.status.online}
      />
    </LeftContent>
  );
}

export default Resume;
